// ── Offline authentication helpers ───────────────────────────────────────────
// We store a SHA-256 hash of the user's offline PIN in localStorage.
// This never leaves the device and is never sent to the server.

const OFFLINE_CREDS_KEY = "offline_credentials";

/**
 * Hash a plain-text PIN using SHA-256 (Web Crypto — no external deps).
 */
export async function hashPin(pin) {
  const encoder = new TextEncoder();
  const data = encoder.encode(pin);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Save offline credentials after a successful online login.
 * @param {string} email
 * @param {string} pin  — plain text PIN entered by user
 * @param {string} role
 * @param {string} name
 */
export async function saveOfflineCredentials(email, pin, role, name) {
  const hash = await hashPin(pin);
  const creds = { email: email.toLowerCase(), hash, role, name };
  localStorage.setItem(OFFLINE_CREDS_KEY, JSON.stringify(creds));
}

/**
 * Verify a PIN against stored offline credentials.
 * Returns the stored creds object if valid, null otherwise.
 */
export async function verifyOfflinePin(email, pin) {
  const raw = localStorage.getItem(OFFLINE_CREDS_KEY);
  if (!raw) return null;
  try {
    const creds = JSON.parse(raw);
    if (creds.email !== email.toLowerCase()) return null;
    const hash = await hashPin(pin);
    if (hash !== creds.hash) return null;
    return creds;
  } catch {
    return null;
  }
}

/**
 * Check whether offline credentials exist for any account.
 */
export function hasOfflineCredentials() {
  return Boolean(localStorage.getItem(OFFLINE_CREDS_KEY));
}

/**
 * Get the stored email so we can pre-fill it offline.
 */
export function getOfflineEmail() {
  const raw = localStorage.getItem(OFFLINE_CREDS_KEY);
  if (!raw) return "";
  try {
    return JSON.parse(raw).email || "";
  } catch {
    return "";
  }
}
